/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { ListFieldset, ListLabel } from '../list/CommonListFieldStyles';
import NestedOptionCheckbox from './NestedOptionCheckbox';
import NestedSelectAllCheckbox from './NestedSelectAllCheckbox';

interface NestedCheckboxProps {
    legend: string;
    selectAllLabel?: string;
    activeValues: string[];
    options: Array<{
        label: string;
        value: string;
        subOptions?: Array<{ label: string; value: string }>;
    }>;
    onUpdate: (updatedValues: string[]) => void;
}

export default function NestedCheckbox({
    legend,
    selectAllLabel,
    options,
    activeValues = [],
    onUpdate,
}: NestedCheckboxProps) {
    const state = mapActiveValuesToState(options, activeValues);

    const handleParentChange = (parentValue: string, hasSubOptions: boolean) => {
        const isChecked = !state[parentValue];
        const updatedValues = updateParentValues(activeValues, options, parentValue, isChecked);
        onUpdate(updatedValues);
    };

    const handleSubOptionChange = (parentValue: string, subValue: string) => {
        const updatedValues = updateSubOptionValues(activeValues, options, parentValue, subValue);
        onUpdate(updatedValues);
    };

    const handleSelectAllChange = () => {
        const allChecked = areAllOptionsChecked(state, options);
        const updatedValues = toggleAllValues(activeValues, options, !allChecked);
        onUpdate(updatedValues);
    };

    return (
        <Scrollable>
            <ListFieldset>
                <ListLabel as="legend">{legend}</ListLabel>
                {selectAllLabel && (
                    <NestedSelectAllCheckbox
                        checked={areAllOptionsChecked(state, options)}
                        indeterminate={areSomeOptionsChecked(state, options) && !areAllOptionsChecked(state, options)}
                        onChange={handleSelectAllChange}
                        label={selectAllLabel}
                    />
                )}
                {options &&
                    options.map((option) => (
                        <NestedOptionCheckbox
                            key={option.value}
                            option={option}
                            state={state}
                            handleParentChange={handleParentChange}
                            handleSubOptionChange={handleSubOptionChange}
                        />
                    ))}
            </ListFieldset>
        </Scrollable>
    );
}

const mapActiveValuesToState = (
    options: NestedCheckboxProps['options'] = [],
    activeValues: string[] = []
): Record<string, boolean> =>
    options.reduce(
        (acc, option) => {
            acc[option.value] = activeValues.includes(option.value);
            if (option.subOptions) {
                option.subOptions.forEach((subOption) => {
                    acc[subOption.value] = activeValues.includes(subOption.value);
                });
            }
            return acc;
        },
        {} as Record<string, boolean>
    );

const updateParentValues = (
    activeValues: string[],
    options: NestedCheckboxProps['options'],
    parentValue: string,
    isChecked: boolean
): string[] => {
    const parentOption = options.find((opt) => opt.value === parentValue);
    let updatedValues = [...activeValues];

    if (isChecked) {
        // Add parent and all sub-options
        updatedValues = Array.from(
            new Set([...updatedValues, parentValue, ...(parentOption?.subOptions?.map((sub) => sub.value) || [])])
        );
    } else {
        // Remove parent and all sub-options
        updatedValues = updatedValues.filter(
            (value) => value !== parentValue && !parentOption?.subOptions?.some((sub) => sub.value === value)
        );
    }

    return updatedValues;
};

const updateSubOptionValues = (
    activeValues: string[],
    options: NestedCheckboxProps['options'],
    parentValue: string,
    subValue: string
): string[] => {
    const parentOption = options.find((opt) => opt.value === parentValue);
    let updatedValues = [...activeValues];

    if (updatedValues.includes(subValue)) {
        // Remove sub-option
        updatedValues = updatedValues.filter((value) => value !== subValue);
    } else {
        // Add sub-option
        updatedValues = [...updatedValues, subValue];
    }

    // Check if all siblings are selected to toggle parent
    if (parentOption?.subOptions?.every((sub) => updatedValues.includes(sub.value))) {
        updatedValues = [...updatedValues, parentValue];
    } else {
        updatedValues = updatedValues.filter((value) => value !== parentValue);
    }

    return updatedValues;
};

const toggleAllValues = (
    activeValues: string[],
    options: NestedCheckboxProps['options'],
    isChecked: boolean
): string[] => {
    if (isChecked) {
        // Select all options and sub-options
        return Array.from(
            new Set(options.flatMap((option) => [option.value, ...(option.subOptions?.map((sub) => sub.value) || [])]))
        );
    }
    // Deselect all
    return [];
};

const areAllOptionsChecked = (state: Record<string, boolean>, options: NestedCheckboxProps['options']) =>
    options.every((option) => state[option.value] && option.subOptions?.every((sub) => state[sub.value]) !== false);

const areSomeOptionsChecked = (state: Record<string, boolean>, options: NestedCheckboxProps['options']) =>
    options.some((option) => state[option.value] || option.subOptions?.some((sub) => state[sub.value]) === true);

const Scrollable = styled('div')`
    overflow-x: auto;
    padding: 0 0.25rem;
`;
